import { Divider, IconButton, Menu, MenuItem } from '@material-ui/core'
import React, { useEffect } from 'react'
import Scrollbars from 'react-custom-scrollbars'
import { Trans, WithTranslation } from 'react-i18next'
import {
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiSave,
  FiTrash,
} from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import { ArrowButton } from '../../../components/Calendar/RoomModal'
import { Desktop, is, Mobile } from '../../../components/MediaQuery'
import Button from '../../../components/Template/Button'
import Modal, {
  CloseButton,
  ModalHeader,
  ModalHeaderLabel,
} from '../../../components/Template/Modal'
import SpinningIcon from '../../../components/Template/SpinningIcon'
import { IRoom } from '../../../models/Room'
import DeleteModal from '../DeleteModal'
import { Modals } from '../../Modals/ModalContainer'
import useSize from '../../../hooks/useSize'
import { Mixpanel } from '../../../utils/Mixpanel'
import { useSelector } from 'react-redux'
import { getUserId } from '../../../reducers/userAccount/selectors/userAccount'

export default function RoomModal({
  open,
  close,
  modalId,
  submitForm,
  children,
  type,
  deleteRoom,
  nextRoom,
  prevRoom,
  roomId,
  iCalendar,
  booking,
  isSaving = false,
  modalOpen,
  modalClose,
  events = 0,
}: IRoomModalProps &
  IRoomModalDispatch & { children: React.ReactNode } & WithTranslation) {
  const [menu, toggleMenu] = React.useState(false)
  const menuAnchor = React.useRef(null)
  const size = useSize()
  const isDesktop = is.desktop()
  const userId = useSelector(getUserId)

  useEffect(() => {
    if (type === 'update') {
      Mixpanel.people.increment(userId, 'Opened Rooms', 1)
      Mixpanel.track('Room Form View', {
        'Room ID': Number(roomId),
      })
    } else {
      Mixpanel.track('Add New Room Screen', {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, roomId])

  function expandMenu() {
    toggleMenu(true)
  }

  function closeMenu() {
    toggleMenu(false)
  }

  function closeDeleteModal(id) {
    modalClose(id)
  }

  function onDelete() {
    Mixpanel.track('Room Delete Menu Item Used', {
      'Room ID': Number(roomId),
    })

    modalOpen(
      Modals.RoomDeleteModal,
      <DeleteModal
        roomId={roomId}
        close={closeDeleteModal}
        closeModal={closeDeleteModal}
        onDelete={deleteRoom}
        events={events}
        booking={booking}
        iCalendar={iCalendar}
      />,
    )
    toggleMenu(false)
  }

  function closeRoomModal() {
    if (type === 'update') {
      Mixpanel.track('Room Form Exit', {
        'Room ID': Number(roomId),
      })
      Mixpanel.people.increment(userId, 'Room Form Exits', 1)
    } else {
      Mixpanel.track('New Room Abandoned', {})
      Mixpanel.people.increment(userId, 'Room Form Abandonments', 1)
    }
    modalClose(Modals.RoomDeleteModal)
    close(modalId)
  }

  return (
    <Modal
      size={size}
      open={open}
      onClose={closeRoomModal}
      style={{
        maxWidth: '80em',
        overflow: 'hidden',
        paddingTop: `${size}em`,
        width: `92vw`,
      }}
      key={roomId}
    >
      <ModalHeader style={isDesktop ? {} : { alignItems: 'flex-start' }}>
        <Flex style={{ width: '100%' }} justifyStart={true}>
          <ModalHeaderLabel
            style={
              isDesktop
                ? { fontSize: `${16 * size}px` }
                : { fontSize: `${16 * size}px`, paddingTop: `${13 * size}px` }
            }
          >
            {type === 'create' ? (
              <Trans i18nKey={'add_room'} />
            ) : (
              <Trans i18nKey={'edit_room_name'} />
            )}
          </ModalHeaderLabel>
        </Flex>
        <Flex
          style={{ width: '100%' }}
          justifyEnd={true}
          alignCenter={isDesktop}
          alignStart={!isDesktop}
        >
          <Desktop>
            {type === 'update' && (
              <React.Fragment>
                <IconButton
                  style={{
                    marginRight: `${15 * size}px`,
                    padding: `${12 * size}px`,
                  }}
                  onClick={onDelete}
                >
                  <FiTrash color={'red'} size={18 * size} />
                </IconButton>
              </React.Fragment>
            )}

            <Button
              size={size}
              as={'button'}
              type={'submit'}
              primary={true}
              onClick={submitForm}
              disabled={isSaving}
            >
              <div style={{ marginRight: `${15 * size}px` }}>
                {isSaving ? (
                  <SpinningIcon size={`${16 * size}px`} />
                ) : (
                  <FiSave size={`${16 * size}px`} />
                )}
              </div>
              <Trans i18nKey={'save'}>Zapisz</Trans>
            </Button>
            {type === 'update' && (
              <Flex
                justifyBetween={true}
                row={true}
                style={{
                  marginLeft: `${30 * size}px`,
                  marginRight: `${10 * size}px`,
                  width: `${53 * size}px`,
                }}
              >
                <ArrowButton size={size} onClick={prevRoom}>
                  <FiChevronLeft size={`${16 * size}px`} />
                </ArrowButton>

                <ArrowButton size={size} onClick={nextRoom}>
                  <FiChevronRight size={`${16 * size}px`} />
                </ArrowButton>
              </Flex>
            )}
          </Desktop>
          {type === 'update' && (
            <Mobile>
              <IconButton
                style={{
                  margin: `0px ${-2 * size}px`,
                  padding: `${12 * size}px`,
                }}
                onClick={prevRoom}
              >
                <FiChevronLeft size={`${20 * size}px`} />
              </IconButton>
              <IconButton
                style={{
                  margin: `0px ${-2 * size}px`,
                  padding: `${12 * size}px`,
                }}
                onClick={nextRoom}
              >
                <FiChevronRight size={`${20 * size}px`} />
              </IconButton>

              <div ref={menuAnchor} style={{ marginLeft: `${10 * size}px` }} />
              <IconButton
                style={{
                  margin: `0px ${-2 * size}px`,
                  padding: `${12 * size}px`,
                }}
                onClick={expandMenu}
              >
                <FiMoreVertical size={`${20 * size}px`} />
              </IconButton>

              <Menu
                anchorEl={menuAnchor.current}
                open={menu}
                onClose={closeMenu}
              >
                <MenuItem onClick={onDelete}>
                  <FiTrash
                    style={{ marginRight: '10px' }}
                    color={'red'}
                    size={18}
                  />
                  <Trans i18nKey={'z677_delete_a_room'} />
                </MenuItem>
              </Menu>
            </Mobile>
          )}
          <CloseButton
            size={size}
            style={{
              marginRight: `${-16 * size}px`,
              ...(isDesktop ? {} : { marginTop: `${3 * size}px` }),
            }}
            onClose={closeRoomModal}
          />
        </Flex>
      </ModalHeader>

      <Scrollbars
        style={
          isDesktop
            ? { minHeight: `calc(85vh - 65px)` }
            : { minHeight: `calc(65vh - 65px)` }
        }
        // ref={this.guestScroll}
        hideTracksWhenNotNeeded={true}
        autoHide={false}
      >
        <div style={{ paddingRight: '10px' }}>{children}</div>
      </Scrollbars>

      <Mobile>
        <Divider style={{ margin: `${15 * size}px 0px` }} />
        <Flex full={true} row={true}>
          <Button
            size={size}
            onClick={closeRoomModal}
            light={true}
            style={{ height: `${38 * size}px`, width: '50%' }}
          >
            <Trans i18nKey={'application_block_dialog_close_btn'} />
          </Button>
          <Button
            style={{ height: `${38 * size}px`, width: '50%' }}
            as={'button'}
            size={size}
            type={'submit'}
            primary={true}
            onClick={submitForm}
            disabled={isSaving}
          >
            {isSaving ? (
              <SpinningIcon
                size={`${16 * size}px`}
                style={{
                  marginRight: `${5 * size}px`,
                  minHeight: `${16 * size}px`,
                  minWidth: `${16 * size}px`,
                }}
              />
            ) : (
              <FiSave
                size={`${16 * size}px`}
                style={{
                  marginRight: `${5 * size}px`,
                  minHeight: `${16 * size}px`,
                  minWidth: `${16 * size}px`,
                }}
              />
            )}
            <Trans i18nKey={'save'}>Zapisz</Trans>
          </Button>
        </Flex>
      </Mobile>
    </Modal>
  )
}

export interface IRoomModalProps {
  open?: boolean
  type?: string
  modalId?: string
  roomId?: IRoom[keyof Pick<IRoom, 'id'>]
  iCalendar?: boolean
  booking?: boolean
  events?: number
  isSaving?: boolean
}

export interface IRoomModalDispatch {
  submitForm: () => void
  close?: (id) => void
  deleteRoom?: () => void
  nextRoom?: () => void
  prevRoom?: () => void
  modalOpen?: (id, element) => void
  modalClose?: (id) => void
}
